import { Autocomplete, Paper, Tooltip, Typography } from '@mui/material';
import { saveUserInfo } from '../../../utils/api/saveUserInfo';
import checked_checkbox from '../../../assets/images/checked_checkbox.svg';
import unchecked_checkbox from '../../../assets/images/unchecked_checkbox.svg';
import magnifying_glass from '../../../assets/images/magnifying_glass.svg';
import { useEffect, useRef, useState } from 'react';
import { isDefaultSharpBooks, getSharpBooksDisplayValue, convertWagerWizSelectionToBackend, convertBackendToWagerWizDisplay, getSharpBooksDisplayCount } from '../../../utils/constants';

import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import { UpgradeModal } from '../../../components/UpgradeModal/UpgradeModal';
import zIndex from '@mui/material/styles/zIndex';

const StyledPopper = styled(Popper)({
	zIndex: "13000 !important", // make sure it's above everything else if needed
});

const CustomPopper = (props) => {
	const { filterName, ...otherProps } = props;
	// For Sharp Books, center the dropdown; for others, use bottom-start
	const placement = filterName === 'sharpBooks' ? 'bottom' : 'bottom-start';
	return <StyledPopper {...otherProps} placement={placement} modifiers={[]} />;
};

const autocompleteStyling = {
	minWidth: 180,
	// width: 'max-content',
	zIndex: 10000,
		'&.MuiAutocomplete-root': {
			backgroundColor: '#17191d',
			color: '#eff1f5 !important',
		},
		'&.MuiInputBase-input': { color: '#eff1f5' },
		borderRadius: '0.5rem',
		'& .MuiAutocomplete-popupIndicator': {
			color: '#eff1f5',
			transition: 'transform 0.2s ease',
		},
		'& .MuiAutocomplete-popupIndicatorOpen': {
			transform: 'rotate(180deg)',
		},
		'&.MuiChip-root': {
			color: '#eff1f5',
		},
		'& .MuiAutocomplete-tag': { color: 'red' },
		'& .MuiChip-root': { 
			color: '#eff1f5', 
			backgroundColor: '#22262e',
			borderRadius: '8px',
			fontSize: '12px',
			fontWeight: '500',
			'&:hover': {
				backgroundColor: '#2d3748',
			}
		},
		'& .MuiAutocomplete-clearIndicator': {
			color: '#eff1f5',
			transition: 'all 0.2s ease',
			'&:hover': {
				backgroundColor: 'rgba(239, 68, 68, 0.1)',
				color: '#ef4444',
			}
		},
		'& .MuiChip-deleteIcon': {
			color: '#eff1f5',
			transition: 'all 0.2s ease',
			'&:hover': {
				backgroundColor: 'rgba(239, 68, 68, 0.1)',
				color: '#ef4444',
			}
		},
		'& .MuiInputBase-input': {
			color: '#eff1f5', // Default input text color
			fontSize: '14px',
			fontWeight: '400',
		},
		'& .MuiInputBase-input:focus': {
			color: '#eff1f5', // Change color when focused
		},
		'& .MuiOutlinedInput-root': {
			border: '1px solid #363844',
			borderRadius: '8px',
			transition: 'all 0.2s ease',
			'&:hover': {
				borderColor: '#1e355e', // On hover
				boxShadow: '0 2px 8px rgba(30, 53, 94, 0.1)',
			},
			'&.Mui-focused': {
				borderColor: '#0b63f4', // On focus
				boxShadow: '0 0 0 3px rgba(11, 99, 244, 0.1)',
			},
	},
	width: document.documentElement.clientWidth / 6 + 40,
};

const CustomPaper = (props) => {
	const { filterName, ...otherProps } = props;
	const isSharpBooks = filterName === 'sharpBooks';
	
	return <Paper {...otherProps} style={{ 
		backgroundColor: '#17191d', 
		borderRadius: '12px', 
		padding: '8px',
		border: '1px solid #363844',
		boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 10px rgba(0, 0, 0, 0.2)',
		backdropFilter: 'blur(10px)',
		maxWidth: isSharpBooks ? '320px' : 'none',
		width: isSharpBooks ? '320px' : 'auto',
		minWidth: isSharpBooks ? '280px' : 'auto'
	}} />;
};

const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export const CustomAutoCompleteFilter = (props) => {
	const {
		options,
		value,
		whichFiltersOpen,
		setWhichFiltersOpen,
		filters,
		index,
		setFilters,
		email,
		isDiscord,
		filterName,
		filterTitle,
		filterPlaceholder,
		newQuery,
		newQueryName,
		isNewQuery,
		setNewQuery,
		tooltip,
		isArbitrage,
		groupingProperty,
		autoOpen = false,
	} = props;

	const inputRef = useRef(`input-${filterName}`);
	// console.log('inputRef: ', inputRef);
	const [isOpen, setIsOpen] = useState(autoOpen);
	// console.log('isOpen: ', isOpen, filterName);
	const [inputValue, setInputValue] = useState('');


	const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
	const [upgradeModalMessage, setUpgradeModalMessage] = useState('');
	const [searchTerm, setSearchTerm] = useState('');

	const user = JSON.parse(window.localStorage.getItem('userdb'));
	const isMasterOrOracle =
		(user?.stripe_plan?.nickname?.includes('Visionary') &&
			user?.stripe_plan.active &&
			user?.stripe_subscription !== null &&
			user?.stripe_status !== 'deleted' &&
			user.stripe_status !== 'past_due') ||
		(user?.stripe_plan?.nickname?.includes('Oracle') &&
			user?.stripe_plan.active &&
			user?.stripe_subscription !== null &&
			user?.stripe_status !== 'deleted' &&
			user.stripe_status !== 'past_due');

	useEffect(() => {
		if (isOpen) {
			inputRef.current.focus();
		}
	}, [isOpen]);

	useEffect(() => {
		function handleClickOutside(event) {
			const autocompletePopper = document.querySelector('.MuiAutocomplete-popper');
			const autocompleteRoot = document.querySelector('.MuiAutocomplete-root');
			const clearAllButton = document.querySelector('.clear-all-button');
			const selectAllButton = document.querySelector('.select-all-button');

			if (
				inputRef.current &&
				!inputRef.current.contains(event.target) &&
				(!autocompletePopper || !autocompletePopper.contains(event.target)) &&
				(!clearAllButton || !clearAllButton.contains(event.target)) &&
				(!selectAllButton || !selectAllButton.contains(event.target)) &&
				(!autocompleteRoot || !autocompleteRoot.contains(event.target)) &&
				event.target.textContent !== filterTitle
			) {
				setIsOpen(false);
			}
		}

		function handleEscape(event) {
			if (event.key === 'Escape') {
				if (inputRef.current && !inputRef.current.contains(event.target)) {
					setIsOpen(false);
				}
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	if (isNewQuery) {
		return (
			<>
				<div className="flex flex-col">
					{filterTitle && (
						<Tooltip title={tooltip}>
							<div className="text-xl mb-3">{filterTitle}</div>
						</Tooltip>
					)}
					<Autocomplete
						disablePortal={false}
						disableCloseOnSelect
						value={filterName === 'sharpBooks' ? convertBackendToWagerWizDisplay(value || []) : (value || [])}
						options={options}
						multiple
						isOptionEqualToValue={(option, value) => option.value === value.value}
						open={isOpen}
						sx={autocompleteStyling}
						inputValue={inputValue}
						onInputChange={(event, newInputValue, reason) => {
							if (reason !== 'reset' && reason !== 'selectOption'  && reason !== 'removeOption') {
								setInputValue(newInputValue);
							}
						}}
						filterOptions={(options, state) => {
							return options.filter((option) => option.label.toLowerCase().includes(state.inputValue.toLowerCase()));
						}}
						renderInput={(params) => {
							const { inputProps, InputProps } = params;
							inputProps.ref.current = inputRef.current;
							return (
								<div
									ref={InputProps.ref}
									style={{
										border: isOpen ? '2px solid #0b63f4' : '1px solid #363844',
										minHeight: '59px',
										padding: '12px',
										borderRadius: '8px',
										display: 'flex',
										flexWrap: 'wrap',
										gap: '8px',
										backgroundColor: isOpen ? 'rgba(30, 53, 94, 0.1)' : '#17191d',
										transition: 'all 0.2s ease',
										boxShadow: isOpen ? '0 4px 12px rgba(11, 99, 244, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
									}}
									className="flex flex-col"
									onClick={(e) => {
										e.stopPropagation(); // Prevent event bubbling to parent components
										if (inputRef.current) {
											inputProps.ref.current = inputRef.current;

											inputProps.ref.current.focus();
											const copy = JSON.parse(JSON.stringify(whichFiltersOpen));

											copy[filterName] = !copy[filterName];

											setWhichFiltersOpen(copy);

											setIsOpen(!isOpen);
										}
									}}>
									<div className="flex flex-row" style={{ alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
										{filterTitle && (
											<div className="cursor-pointer" style={{ 
												alignContent: 'center', 
												fontSize: filterTitle === 'Sportsbooks' && isOpen ? '10px' : '14px',
												fontWeight: '500',
												color: '#eff1f5',
												letterSpacing: '0.025em',
												flexShrink: 0
											}}>
												{filterTitle}
											</div>
										)}

										<div style={{ 
											borderRadius: '12px', 
											padding: '4px 10px', 
											backgroundColor: '#0b63f4', 
											cursor: 'pointer', 
											height: '24px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontSize: '12px',
											fontWeight: '600',
											color: '#ffffff',
											boxShadow: '0 2px 4px rgba(11, 99, 244, 0.3)',
											minWidth: '20px',
											flexShrink: 0
										}}>
											{filterName === 'sharpBooks' 
												? getSharpBooksDisplayCount(newQuery?.[index]?.[filterName] || [])
												: newQuery?.[index]?.[filterName]?.length || 0
											}
										</div>
										{isOpen && (
																							<button
													onClick={async (e) => {
														e.stopPropagation();
														const copy = JSON.parse(JSON.stringify(newQuery));
														
														// Special handling for sharp books - set to defaults instead of empty
														if (filterName === 'sharpBooks') {
															copy[index][filterName] = [
																{ label: 'WagerWiz', value: 'WagerWiz' }
															];
														} else {
															copy[index][filterName] = [];
														}
														
														setFilters(copy);
														setNewQuery(copy);

														if (!isArbitrage) {
															await saveUserInfo(copy, email, isDiscord);
														}
													}}
												className="clear-all-button"
												style={{
													backgroundColor: 'rgba(239, 68, 68, 0.1)',
													border: '1px solid #ef4444',
													borderRadius: '6px',
													color: '#ef4444',
													padding: '6px 12px',
													fontSize: '12px',
													fontWeight: '500',
													cursor: 'pointer',
													transition: 'all 0.2s ease',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													minWidth: '80px',
													flexShrink: 0
												}}
												onMouseEnter={(e) => {
													e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
													e.target.style.transform = 'translateY(-1px)';
												}}
												onMouseLeave={(e) => {
													e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
													e.target.style.transform = 'translateY(0)';
												}}>
												Clear All
											</button>
										)}
									</div>

									<div className="flex flex-row" style={{ 
										border: '1px solid #363844', 
										borderRadius: '8px', 
										padding: '8px 12px', 
										display: isOpen ? '' : 'none',
										backgroundColor: 'rgba(54, 56, 68, 0.3)',
										backdropFilter: 'blur(10px)',
										alignItems: 'center',
										gap: '8px',
										flex: '1',
										minWidth: '0'
									}}>
										<span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
											<img src={magnifying_glass} alt="magnifying glass" style={{ width: '14px', height: '14px', opacity: 0.7 }} />
										</span>
										<input
											type="text"
											{...inputProps}
											style={{
												border: 'none',
												outline: 'none',
												backgroundColor: 'transparent',
												flex: '1',
												minWidth: '0',
												fontSize: '12px',
												color: '#eff1f5',
												fontWeight: '400',
												'::placeholder': {
													color: '#9ca3af',
													opacity: 0.7
												}
											}}
											onClick={(e) => {
												e.stopPropagation();
											}}
											onKeyDown={(e) => {
												// Prevent removing last selected value on Backspace when input is empty
												if (e.key === 'Backspace' && e.currentTarget.value === '') {
													e.stopPropagation();
												}
											}}
											placeholder={`Search for a ${filterPlaceholder}`}
											ref={inputRef}
										/>
									</div>
								</div>
							);
						}}
						onChange={async (e, newValue) => {
							const limitFilters = ['sportsbooks'];
							const isLimitedFilter = limitFilters.includes(filterName);

							if (!isMasterOrOracle && isLimitedFilter && newValue.length > 5) {
								newValue = newValue.slice(0, 5);
								setIsUpgradeModalOpen(true);
							}

							// Special handling for sharp books - if cleared, set to defaults
							if (filterName === 'sharpBooks' && newValue.length === 0) {
								newValue = [
									{ label: 'WagerWiz', value: 'WagerWiz' }
								];
							}

							// Convert WagerWiz display selection to backend values
							let backendValue = newValue;
							if (filterName === 'sharpBooks') {
								backendValue = convertWagerWizSelectionToBackend(newValue);
							}

							let copy;

							try {
								copy = JSON.parse(JSON.stringify(isNewQuery ? newQuery : filters));
								if (!copy[index]) copy[index] = {};
								copy[index][filterName] = [...new Map(backendValue.map((item) => [item.value, item])).values()];
								if (isNewQuery) copy[index].name = newQueryName;
							} catch (e) {
								copy = { [index]: { [filterName]: [...new Map(backendValue.map((item) => [item.value, item])).values()] } };
								if (isNewQuery) copy[index].name = newQueryName;
							}

							if (isNewQuery) {
								setNewQuery(copy);
							} else {
								setFilters(copy);
							}
							if (!isArbitrage) {
								await saveUserInfo(copy, email, isDiscord);
							}
							
							// Keep the autocomplete open after selection
							setIsOpen(true);
						}}
						renderOption={(props, option) => {
							const currentValues = filterName === 'sharpBooks' 
								? convertBackendToWagerWizDisplay(newQuery[index][filterName] || [])
								: (newQuery[index][filterName] || []);
							const checked = currentValues.some((filt) => filt.label === option.label);
							return (
								<div style={{ color: 'white' }} {...props}>
									<img style={{ marginRight: '0.5rem' }} src={checked ? checked_checkbox : unchecked_checkbox} alt="checkbox" />{' '}
									<Typography style={{ fontWeight: checked ? 'bold' : '' }} variant="body1">
										{option.label}
									</Typography>
								</div>
							);
						}}
						slots={{
							paper: (props) => <CustomPaper {...props} filterName={filterName} />,
							popper: (props) => <CustomPopper {...props} filterName={filterName} />,
							noOptions: {
								sx: {
									color: 'white',
									padding: 1,
								},
							},
						}}
					/>
				</div>
			</>
		);
	} else {
		const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
		return (
			<>
				<UpgradeModal isOpen={isUpgradeModalOpen} setIsOpen={setIsUpgradeModalOpen} message={upgradeModalMessage} />
				<div className="flex flex-col">
					{filterTitle && (
						<Tooltip title={tooltip}>
							<div className="text-xl mb-3">{filterTitle}</div>
						</Tooltip>
					)}
					<Autocomplete
						disablePortal={false}
						disableCloseOnSelect
						value={filterName === 'sharpBooks' ? convertBackendToWagerWizDisplay(value || []) : (value || [])}
						options={options}
						multiple
						isOptionEqualToValue={(option, value) => option.value === value.value}
						open={isOpen}
						sx={autocompleteStyling}
						inputValue={inputValue}
						onInputChange={(event, newInputValue, reason) => {
							if (reason !== 'reset' && reason !== 'selectOption'  && reason !== 'removeOption') {
								setInputValue(newInputValue);
							}
						}}
						filterOptions={(options, state) => {
							return options.filter((option) => option.label.toLowerCase().includes(state.inputValue.toLowerCase()));
						}}
						{...(groupingProperty
							? {
									groupBy: (option) => option[groupingProperty],
									renderGroup: (params) => (
										<li key={params.key}>
											<Typography variant="subtitle2" sx={{ padding: '4px 8px', color: '#aaa' }}>
												{capitalizeFirst(params.group)}
											</Typography>
											<ul style={{ paddingLeft: 0 }}>{params.children}</ul>
										</li>
									),
							  }
							: {})}
						renderInput={(params) => {
							const { inputProps, InputProps } = params;
							inputProps.ref.current = inputRef.current;
							return (
								<div
									ref={InputProps.ref}
									style={{
										border: isOpen ? '2px solid #0b63f4' : '1px solid #363844',
										minHeight: '59px',
										padding: '12px',
										borderRadius: '8px',
										display: 'flex',
										flexWrap: 'wrap',
										gap: '8px',
										backgroundColor: isOpen ? 'rgba(30, 53, 94, 0.1)' : '#17191d',
										transition: 'all 0.2s ease',
										boxShadow: isOpen ? '0 4px 12px rgba(11, 99, 244, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
									}}
									className="flex flex-col"
									onClick={(e) => {
										e.stopPropagation(); // Prevent event bubbling to parent components
										if (inputRef.current) {
											inputProps.ref.current = inputRef.current;

											inputProps.ref.current.focus();
											const copy = JSON.parse(JSON.stringify(whichFiltersOpen));

											copy[filterName] = !copy[filterName];

											setWhichFiltersOpen(copy);

											setIsOpen(!isOpen);
										}
									}}>
									<div className="flex flex-row" style={{ alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
										{filterTitle && (
											<div className="cursor-pointer" style={{ 
												alignContent: 'center', 
												fontSize: filterTitle === 'Sportsbooks' && isOpen ? '10px' : '14px',
												fontWeight: '500',
												color: '#eff1f5',
												letterSpacing: '0.025em',
												flexShrink: 0
											}}>
												{filterTitle}
											</div>
										)}

										<div style={{ 
											borderRadius: '12px', 
											padding: '4px 10px', 
											backgroundColor: '#0b63f4', 
											cursor: 'pointer', 
											height: '24px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontSize: '12px',
											fontWeight: '600',
											color: '#ffffff',
											boxShadow: '0 2px 4px rgba(11, 99, 244, 0.3)',
											minWidth: '20px',
											flexShrink: 0
										}}>
											{filterName === 'sharpBooks' 
												? getSharpBooksDisplayCount(filters?.[index]?.[filterName] || [])
												: filters?.[index]?.[filterName]?.length || 0
											}
										</div>
										{isOpen && (
											<div
												onClick={(e) => {
													e.stopPropagation();
												}}
												className="flex flex-row gap-2" style={{ flexShrink: 0 }}>
												<button
													onClick={(e) => {
														e.stopPropagation();
														const copy = JSON.parse(JSON.stringify(filters));
														copy[index][filterName] = [...filteredOptions];
														setFilters(copy);
													}}
													className="select-all-button"
													style={{
														backgroundColor: 'rgba(11, 99, 244, 0.1)',
														border: '1px solid #0b63f4',
														borderRadius: '6px',
														color: '#0b63f4',
														padding: '6px 12px',
														fontSize: '12px',
														fontWeight: '500',
														cursor: 'pointer',
														transition: 'all 0.2s ease',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														minWidth: '80px'
													}}
													onMouseEnter={(e) => {
														e.target.style.backgroundColor = 'rgba(11, 99, 244, 0.2)';
														e.target.style.transform = 'translateY(-1px)';
													}}
													onMouseLeave={(e) => {
														e.target.style.backgroundColor = 'rgba(11, 99, 244, 0.1)';
														e.target.style.transform = 'translateY(0)';
													}}>
													Select All
												</button>

												<button
													onClick={async (e) => {
														e.stopPropagation();
														const copy = JSON.parse(JSON.stringify(filters));
														
														// Special handling for sharp books - set to defaults instead of empty
														if (filterName === 'sharpBooks') {
															copy[index][filterName] = [
																{ label: 'WagerWiz Props', value: 'WagerWiz Props' },
																{ label: 'WagerWiz Mains', value: 'WagerWiz Mains' }
															];
														} else {
															copy[index][filterName] = [];
														}
														
														setFilters(copy);
														
														if (!isArbitrage) {
															await saveUserInfo(copy, email, isDiscord);
														}
													}}
													className="clear-all-button"
													style={{
														backgroundColor: 'rgba(239, 68, 68, 0.1)',
														border: '1px solid #ef4444',
														borderRadius: '6px',
														color: '#ef4444',
														padding: '6px 12px',
														fontSize: '12px',
														fontWeight: '500',
														cursor: 'pointer',
														transition: 'all 0.2s ease',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														minWidth: '80px'
													}}
													onMouseEnter={(e) => {
														e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
														e.target.style.transform = 'translateY(-1px)';
													}}
													onMouseLeave={(e) => {
														e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
														e.target.style.transform = 'translateY(0)';
													}}>
													Clear All
												</button>
											</div>
										)}
									</div>

									<div
										className="flex flex-row"
										style={{ 
											border: '1px solid #363844', 
											borderRadius: '8px', 
											padding: '8px 12px', 
											display: isOpen ? '' : 'none', 
											backgroundColor: 'rgba(54, 56, 68, 0.3)',
											backdropFilter: 'blur(10px)',
											alignItems: 'center',
											gap: '8px',
											flex: '1',
											minWidth: '0'
										}}>
										<span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
											<img src={magnifying_glass} alt="magnifying glass" style={{ width: '14px', height: '14px', opacity: 0.7 }} />
										</span>
										<input
											type="text"
											{...inputProps}
											style={{
												border: 'none',
												outline: 'none',
												backgroundColor: 'transparent',
												flex: '1',
												minWidth: '0',
												fontSize: '12px',
												color: '#eff1f5',
												fontWeight: '400',
												'::placeholder': {
													color: '#9ca3af',
													opacity: 0.7
												}
											}}
											onClick={(e) => {
												e.stopPropagation();
											}}
											onKeyDown={(e) => {
												// Prevent removing last selected value on Backspace when input is empty
												if (e.key === 'Backspace' && e.currentTarget.value === '') {
													e.stopPropagation();
												}
											}}
											value={searchTerm}
											onChange={(e) => {
												setSearchTerm(e.target.value);
												inputProps.onChange?.(e);
											}}
											placeholder={`Search for a ${filterPlaceholder}`}
											ref={inputRef}
										/>
									</div>
								</div>
							);
						}}
						onChange={async (e, newValue) => {
							const limitFilters = ['sportsbooks', 'leagues'];
							const isLimitedFilter = limitFilters.includes(filterName);
							if (!isMasterOrOracle && isLimitedFilter && filterName === 'sportsbooks' && newValue.length > 5) {
								newValue = newValue.slice(0, 5);
								setUpgradeModalMessage('Only 5 sportsbooks can be selected with your current plan');
								setIsUpgradeModalOpen(true);
							} else if (!isMasterOrOracle && isLimitedFilter && filterName === 'leagues' && newValue.length > 2) {
								newValue = newValue.slice(0, 2);
								setUpgradeModalMessage('Only 2 leagues can be selected with your current plan');
								setIsUpgradeModalOpen(true);
							}

							// Special handling for sharp books - if cleared, set to defaults
							if (filterName === 'sharpBooks' && newValue.length === 0) {
								newValue = [
									{ label: 'WagerWiz', value: 'WagerWiz' }
								];
							}

							// Convert WagerWiz display selection to backend values
							let backendValue = newValue;
							if (filterName === 'sharpBooks') {
								backendValue = convertWagerWizSelectionToBackend(newValue);
							}

							let copy;

							try {
								copy = JSON.parse(JSON.stringify(isNewQuery ? newQuery : filters));
								if (!copy[index]) copy[index] = {};
								copy[index][filterName] = [...new Map(backendValue.map((item) => [item.value, item])).values()];
								if (isNewQuery) copy[index].name = newQueryName;
							} catch (e) {
								copy = { [index]: { [filterName]: [...new Map(backendValue.map((item) => [item.value, item])).values()] } };
								if (isNewQuery) copy[index].name = newQueryName;
							}

							if (isNewQuery) {
								setNewQuery(copy);
							} else {
								setFilters(copy);
							}
							if (!isArbitrage) {
								await saveUserInfo(copy, email, isDiscord);
							}
							
							// Keep the autocomplete open after selection
							setIsOpen(true);
						}}
						renderOption={(props, option) => {
							const currentValues = filterName === 'sharpBooks' 
								? convertBackendToWagerWizDisplay(filters[index][filterName] || [])
								: (filters[index][filterName] || []);
							const checked = currentValues.some((filt) => filt.label === option.label);
							return (
								<div style={{ color: 'white' }} {...props}>
									<img style={{ marginRight: '0.5rem' }} src={checked ? checked_checkbox : unchecked_checkbox} alt="checkbox" />{' '}
									<Typography style={{ fontWeight: checked ? 'bold' : '' }} variant="body1">
										{option.label}
									</Typography>
								</div>
							);
						}}
						slots={{
							paper: (props) => <CustomPaper {...props} filterName={filterName} />,
							popper: (props) => <CustomPopper {...props} filterName={filterName} />,
							noOptions: {
								sx: {
									color: 'white',
									padding: 1,
								},
							},
						}}
						slotProps={{
							popper: {
								modifiers: [],
							},
							paper: {
								sx: {
									'& .MuiAutocomplete-option': {
										'&:hover, &[data-focus="true"], &:focus': {
											backgroundColor: '#4e5563',
										},
									},
									'& .Mui-focusVisible': {
										backgroundColor: '#4e5563 !important',
									},
									'& .Mui-focusVisible:hover': {
										backgroundColor: '#4e5563 !important',
									},
									'& .MuiAutocomplete-option:hover': {
										backgroundColor: '#4e5563 !important',
									},
								},
							},
						}}
					/>
				</div>
			</>
		);
	}
};
